/**
 * Hook สำหรับดึงผลรางวัลหวย — ใช้ร่วมกันระหว่าง LotteryResultPage และ ClassicHomePage
 * ดึงจาก API lottery-rounds + huay-types เหมือนหน้าประกาศผล (announce-results)
 */
import { useState, useEffect, useMemo } from 'react'
import { lotteryService, LotteryRound } from '../services/lotteryService'
import {
  isYeekeeRound,
  isYeekeeVipRound,
  isGovernmentLottery,
  isThaiGovernmentLotteryOnly,
  isEgovRound,
  isGsbRound,
  isRoundVisibleOnDate,
  isRoundNumberDateExactly,
  getSectionKind,
} from '../pages/lottery-result'

/** จำนวนรอบสูงสุดต่อ section — ไม่ fix ที่ 50; รองรับกรณีเช่น หวยยี่กี รอบละ 5 นาที (288 รอบ/วัน) */
const MAX_DISPLAY_ROUNDS_PER_SECTION = 500

/** ช่วงเวลาปัดผลรางวัลอัตโนมัติ (ms) — 0 = ไม่ปัด, ใช้เมื่อต้องการให้ผลอัปเดตเรื่อยๆ แม้ไม่มี activity */
const DEFAULT_POLLING_INTERVAL_MS = 60_000

export interface UseLotteryResultsOptions {
  /** เมื่อมีค่า จะไม่โหลด pool (ใช้ใน LotteryResultPage โหมด roundId) */
  targetRoundId?: string | null
  /** เพิ่มค่าเมื่อ WebSocket แจ้ง huay_type_updated / lottery_round_updated เพื่อ refetch */
  refreshTrigger?: number
  /** อัปเดตผลรางวัลอัตโนมัติทุก N ms (0 = ปิด) */
  pollingIntervalMs?: number
}

export interface UseLotteryResultsReturn {
  poolError: string | null
  latestGovernmentRound: LotteryRound | null
  latestYeekeeRound: LotteryRound | null
  latestEgovRound: LotteryRound | null
  latestGsbRound: LotteryRound | null
  latestSetRounds: LotteryRound[]
  latestThaiStockRounds: LotteryRound[]
  latestForeignRounds: LotteryRound[]
  latestForeignStockRounds: LotteryRound[]
  latestVipRounds: LotteryRound[]
  latestVisaRounds: LotteryRound[]
  allYeekeeVipRounds: LotteryRound[]
}

function roundMatchesHuayType(
  round: LotteryRound,
  huayType: { _id?: string; huayTypeId?: string; name?: string; code?: string }
): boolean {
  const tabId = String(huayType.huayTypeId ?? huayType._id ?? '')
  const roundTypeId = String(round.huayTypeId ?? '')
  if (roundTypeId && tabId && roundTypeId === tabId) return true
  if (round.huayTypeName && huayType.name && String(round.huayTypeName).trim() === String(huayType.name).trim())
    return true
  if (
    round.huayTypeCode &&
    huayType.code &&
    String(round.huayTypeCode).toUpperCase() === String(huayType.code).toUpperCase()
  )
    return true
  return false
}

export function useLotteryResults(
  selectedDate: string,
  options: UseLotteryResultsOptions = {}
): UseLotteryResultsReturn {
  const { targetRoundId = null, refreshTrigger = 0, pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS } = options

  const [pool, setPool] = useState<LotteryRound[]>([])
  const [pollTick, setPollTick] = useState(0)
  const [huayTypes, setHuayTypes] = useState<Array<{ _id: string; huayTypeId?: string; name: string; code?: string; configuration?: { isActive?: boolean } }>>([])
  const [poolError, setPoolError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedDate) return
    let cancelled = false
    setPoolError(null)
    // ใช้ Public API เท่านั้น — ไม่ต้อง login (หน้าหลัก / ผลหวย)
    // ดึงแค่วันต่อวัน (dateFrom/dateTo) หรือ round เดียว (roundId) ไม่ดึงทั้งก้อน
    const load =
      targetRoundId
        ? Promise.all([
            lotteryService.getLotteryRounds({ roundId: targetRoundId, limit: 1 }),
            lotteryService.getTenantHuayTypes({ limit: 100 }),
          ]).then(([list, types]) => ({
            list: list || [],
            types: types || [],
          }))
        : Promise.all([
            lotteryService.getLotteryRounds({
              dateFrom: selectedDate,
              dateTo: selectedDate,
              limit: 200,
              timezone: 'Asia/Bangkok',
            }),
            lotteryService.getTenantHuayTypes({ limit: 100 }),
          ]).then(([list, types]) => ({
            list: list || [],
            types: types || [],
          }))
    load
      .then(({ list, types }) => {
        if (cancelled) return
        setPool(list)
        setHuayTypes(types)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[useLotteryResults] API error:', err)
        setPoolError(err?.message || 'โหลดข้อมูลไม่ได้')
        setPool([])
      })
    return () => { cancelled = true }
  }, [targetRoundId, selectedDate, refreshTrigger, pollTick])

  // อัปเดตผลรางวัลอัตโนมัติเรื่อยๆ แม้ไม่มี activity (polling)
  useEffect(() => {
    if (!pollingIntervalMs || pollingIntervalMs <= 0) return
    const id = setInterval(() => setPollTick((t) => t + 1), pollingIntervalMs)
    return () => clearInterval(id)
  }, [pollingIntervalMs])

  /** เฉพาะประเภทหวยที่เปิดใช้งาน (configuration.isActive !== false) — ใช้แสดง section VIP/VISA/ยี่กี */
  const activeHuayTypes = useMemo(
    () => huayTypes.filter((ht) => (ht as { configuration?: { isActive?: boolean } }).configuration?.isActive !== false),
    [huayTypes]
  )

  const vipHuayType = useMemo(
    () => activeHuayTypes.find((ht) => /หวย\s*vip|vip/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  const visaHuayType = useMemo(
    () => activeHuayTypes.find((ht) => /หวย\s*visa|visa/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  /** ประเภทหวยยี่กี — รองรับชื่อทั้ง admin ("หวยยี่กี") และ display ("จับยี่กี VIP") */
  const yeekeeHuayType = useMemo(
    () =>
      activeHuayTypes.find(
        (ht) =>
          /หวย\s*ยี่กี|หวย\s*ยีกี่|จับ\s*ยี่กี|จับ\s*ยีกี่|yeekee|ยี่กี|ยีกี่/i.test(ht.name || '') ||
          /yeekee|lyk/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  /** ประเภทหวยต่างประเทศ — แสดง section เฉพาะเมื่อมีประเภทที่เปิดอยู่ */
  const foreignHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) =>
          (/ต่างประเทศ|foreign|international/i.test(ht.name || '') || /foreign|international/i.test(ht.code || '')) &&
          !/หุ้น/i.test(ht.name || '')
      ),
    [activeHuayTypes]
  )

  /** ประเภทหวยหุ้น (หุ้นต่างประเทศ / หวยหุ้น) — ไม่รวมหวยหุ้นไทย */
  const foreignStockHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) =>
          /หุ้นต่างประเทศ|foreign\s*stock|stock/i.test(ht.name || '') ||
          (/หุ้น/i.test(ht.name || '') && !/หุ้นไทย|ไทย/i.test(ht.name || '')) ||
          /stock/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  /** ประเภทหวยหุ้นไทย */
  const thaiStockHuayTypes = useMemo(
    () => activeHuayTypes.filter((ht) => /หุ้นไทย|thai\s*stock/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  /** ประเภทหวยชุด — แสดง section เฉพาะเมื่อมีประเภทที่เปิดอยู่ */
  const activeSetHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) => /หวย\s*ชุด|ชุด/i.test(ht.name || '') || /set/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  const pickLatestRounds = useMemo(
    () =>
      (filterFn: (r: LotteryRound) => boolean, maxRounds = 1): LotteryRound[] => {
        if (!pool.length) return []
        const filtered = pool.filter(filterFn)
        if (!filtered.length) return []
        const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
        const source = visible.length > 0 ? visible : filtered
        const sorted = [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime())
        return sorted.slice(0, maxRounds)
      },
    [pool, selectedDate]
  )

  const latestGovernmentRound = useMemo(() => {
    let gov = pickLatestRounds(isThaiGovernmentLotteryOnly, 1)
    if (gov.length === 0) gov = pickLatestRounds(isGovernmentLottery, 1)
    return gov[0] ?? null
  }, [pickLatestRounds])

  const latestEgovRound = useMemo(() => pickLatestRounds(isEgovRound, 1)[0] ?? null, [pickLatestRounds])
  const latestGsbRound = useMemo(() => pickLatestRounds(isGsbRound, 1)[0] ?? null, [pickLatestRounds])

  /** หวยชุด — แสดงเฉพาะเมื่อมีประเภทที่เปิดอยู่ */
  const latestSetRounds = useMemo(() => {
    if (!pool.length || activeSetHuayTypes.length === 0) return []
    const filtered = pool.filter(
      (r) => getSectionKind(r) === 'set' && activeSetHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [pool, activeSetHuayTypes, selectedDate])

  /** หวยหุ้นไทย — แสดงเฉพาะเมื่อมีประเภทที่เปิดอยู่ */
  const latestThaiStockRounds = useMemo(() => {
    if (!pool.length || thaiStockHuayTypes.length === 0) return []
    const filtered = pool.filter(
      (r) => getSectionKind(r) === 'thai_stock' && thaiStockHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [pool, thaiStockHuayTypes, selectedDate])

  /** หวยต่างประเทศ — แสดงเฉพาะเมื่อมีประเภทที่เปิดอยู่ */
  const latestForeignRounds = useMemo(() => {
    if (!pool.length || foreignHuayTypes.length === 0) return []
    const filtered = pool.filter(
      (r) => getSectionKind(r) === 'foreign' && foreignHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [pool, foreignHuayTypes, selectedDate])

  /** หวยหุ้น — แสดงทุกรอบที่ตรงกับประเภทหวยหุ้น (รวมหวยหุ้นไทยในประเภทเดียวกัน) ให้ครบเหมือนใน Admin */
  const latestForeignStockRounds = useMemo(() => {
    if (!pool.length || foreignStockHuayTypes.length === 0) {
      if (process.env.NODE_ENV === 'development' && pool.length > 0) {
        console.log('[useLotteryResults] หวยหุ้น: แสดง static เพราะ', foreignStockHuayTypes.length === 0 ? 'ไม่มีประเภทหวยหุ้นที่เปิดอยู่ (foreignStockHuayTypes ว่าง)' : 'pool ว่าง', {
          poolLength: pool.length,
          foreignStockHuayTypesCount: foreignStockHuayTypes.length,
          activeHuayTypeNames: foreignStockHuayTypes.map((ht) => ht.name),
        })
      }
      return []
    }
    const filtered = pool.filter((r) => foreignStockHuayTypes.some((ht) => roundMatchesHuayType(r, ht)))
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [pool, foreignStockHuayTypes, selectedDate])

  const pickByHuayType = useMemo(
    () =>
      (huayType: { _id?: string; huayTypeId?: string; name?: string; code?: string } | null, max = MAX_DISPLAY_ROUNDS_PER_SECTION): LotteryRound[] => {
        if (!pool.length || !huayType) return []
        const filtered = pool.filter((r) => roundMatchesHuayType(r, huayType))
        if (!filtered.length) return []
        const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
        const source = visible.length > 0 ? visible : filtered
        return [...source].sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()).slice(0, max)
      },
    [pool, selectedDate]
  )

  /** หวย VIP / VISA / ยี่กี ฯลฯ — แสดงครบทุกรอบที่โหลดได้ (ไม่ fix ที่ 50, รองรับรอบละ 5 นาที เป็นต้น) */
  const latestVipRounds = useMemo(() => pickByHuayType(vipHuayType ?? null), [pickByHuayType, vipHuayType])
  const latestVisaRounds = useMemo(() => pickByHuayType(visaHuayType ?? null), [pickByHuayType, visaHuayType])

  // Section "จับยี่กี VIP" = แสดงเฉพาะเมื่อมีประเภทหวยยี่กีที่เปิดอยู่ (ไม่ใช้ fallback เพื่อให้ปิดที่ admin แล้วซ่อนได้)
  const allYeekeeVipRounds = useMemo(() => {
    if (!pool.length || !yeekeeHuayType) return []
    const filtered = pool.filter((r) => roundMatchesHuayType(r, yeekeeHuayType))
    const sameDayOnly = filtered.filter((r) => isRoundNumberDateExactly(r, selectedDate))
    const visible = sameDayOnly.length > 0 ? sameDayOnly : filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    return [...visible].sort((a, b) => new Date(a.betStartTime).getTime() - new Date(b.betStartTime).getTime())
  }, [pool, yeekeeHuayType, selectedDate])

  // Debug: console.log เฉพาะหวยยี่กี (ลบออกได้เมื่อตรวจสอบเสร็จ)
  useEffect(() => {
    if (!pool.length) return
    const byYeekeeType =
      yeekeeHuayType != null ? pool.filter((r) => roundMatchesHuayType(r, yeekeeHuayType)) : []
    console.log('[useLotteryResults] หวยยี่กี (selectedDate=', selectedDate, ')', {
      yeekeeHuayType: yeekeeHuayType ? { name: yeekeeHuayType.name, code: yeekeeHuayType.code, id: yeekeeHuayType.huayTypeId ?? yeekeeHuayType._id } : null,
      allYeekeeVipRoundsCount: allYeekeeVipRounds.length,
      byYeekeeTypeCount: byYeekeeType.length,
      allYeekeeVipRounds: allYeekeeVipRounds.map((r) => ({ _id: r._id, roundNumber: r.roundNumber, huayName: r.huayName, huayTypeName: r.huayTypeName })),
    })
  }, [pool, yeekeeHuayType, selectedDate, allYeekeeVipRounds])

  const latestYeekeeRound = useMemo(() => {
    if (!pool.length) return null
    const isVipRound = vipHuayType
      ? (r: LotteryRound) => roundMatchesHuayType(r, vipHuayType)
      : isYeekeeVipRound
    const yeekeeOnly = pool.filter(
      (r) => isYeekeeRound(r) && !isVipRound(r) && r.status !== 'open'
    )
    const visible = yeekeeOnly.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : yeekeeOnly
    if (!source.length) return null
    const sorted = [...source].sort(
      (a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime()
    )
    return sorted[0]
  }, [pool, vipHuayType, selectedDate])

  return {
    poolError,
    latestGovernmentRound,
    latestYeekeeRound,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  }
}
